<template>
  <div class="main main__container">
    <div class="block__header-smaller">
      {{ t('registeringHeader') }}
    </div>
    <p class="block__text block__text--margin">
      {{ t('registeringDescription') }}
    </p>

    <validation-observer
      ref="observer"
      tag="form">
      <validation-provider v-slot="{ errors }" rules="required">
        <fieldset
          class="input-column__container input-column__container--register"
          aria-required="true"
          :aria-invalid="errors.length ? 'true' : 'false'"
          role="radiogroup">
          <legend class="sr-only">
            {{ tStatic('selectCustomerTypeAria') }}
          </legend>

          <oie-base-radio-checkbox
            v-model="selectedCustomerType"
            value="company"
            name="customer-type"
            area-describedby="customer-type-error"
            :label="t('companyLabel')"></oie-base-radio-checkbox>

          <oie-base-radio-checkbox
            v-model="selectedCustomerType"
            value="private"
            name="customer-type"
            area-describedby="customer-type-error"
            :label="t('privateLabel')"></oie-base-radio-checkbox>

          <div
            v-show="errors.length"
            :id="`customer-type-error`"
            class="body-smaller base-radio-checkbox__error customer-type-error">
            <oie-base-icon icon="warning" size="xs"></oie-base-icon>
            <span v-for="error in errors" :key="error">
              {{ error }}
            </span>
          </div>
        </fieldset>
      </validation-provider>

      <oie-layout-grid class="grid--two">
        <div class="input-column">
          <div class="input-column__container">
            <h3 class="input-column__header">
              {{ t('customerInformationHeader') }}
            </h3>

            <validation-provider
              v-if="useCountryDropDown && (hasMultipleMarkets || clientModel.impersonating)"
              v-slot="{errors}"
              rules="required"
              slim>
              <oie-base-dropdown
                v-if="useCountryDropDown && (hasMultipleMarkets || clientModel.impersonating)"
                v-model="invoiceAddress.countryCode"
                :is-disabled="!hasMultipleMarkets"
                :label-text="`${tStatic('addressCountryLabel')}*`"
                :options="clientModel.countryList"
                :initial-text="createInitialText"
                :use-filter-functionality="clientModel.countryList.length > 10"
                :filter-input-placeholder="tStatic('addressCountryPlaceholder')"
                :error-messages="errors"
                name="register-country"
                container-classes="order-request__dropdown"
                z-index="10">
              </oie-base-dropdown>
            </validation-provider>

            <template v-if="!useCountryDropDown && (hasMultipleMarkets || clientModel.impersonating)">
              <fieldset role="radiogroup" class="input-column__container input-column__container--register">
                <legend class="body-smaller-semi-bold">
                  {{ tStatic('addressCountryLabel') }}
                </legend>
                <oie-base-radio-checkbox
                  v-for="country in clientModel.countryList"
                  :key="'country' + country.value"
                  v-model="invoiceAddress.countryCode"
                  name="country"
                  :value="country.value"
                  :label="country.text"></oie-base-radio-checkbox>
              </fieldset>
            </template>

            <div v-if="clientModel.impersonating" class="input-column__actions">
              <oie-base-dropdown
                v-model="language"
                :is-disabled="clientModel.availibleLanguages.length <= 1"
                :label-text="tStatic('superUserUserPreferredLanguage')"
                :options="clientModel.availibleLanguages"
                :use-filter-functionality="false"
                :default-selected="createDefaultLanguageSelection"
                name="user-language"
                container-classes="order-request__dropdown">
              </oie-base-dropdown>
            </div>

            <validation-provider
              v-if="isCompanyCustomer"
              v-slot="{errors}"
              rules="required|invalidchars"
              slim>
              <oie-base-input-v2
                v-if="isCompanyCustomer"
                v-model="customerName"
                maxlength="100"
                :label="`${t('companyNameLabel')}`"
                required
                :placeholder="t('companyNamePlaceholder')"
                :errors="errors"></oie-base-input-v2>
            </validation-provider>

            <validation-provider
              v-if="!isCompanyCustomer"
              v-slot="{errors}"
              rules="required|invalidchars"
              slim>
              <oie-base-input-v2
                v-if="!isCompanyCustomer"
                v-model="firstName"
                maxlength="49"
                :label="`${t('privateNameLabel')}`"
                required
                :placeholder="t('privateNamePlaceholder')"
                :errors="errors"></oie-base-input-v2>
            </validation-provider>

            <validation-provider
              v-if="!isCompanyCustomer"
              v-slot="{errors}"
              rules="required|invalidchars"
              slim>
              <oie-base-input-v2
                v-if="!isCompanyCustomer"
                v-model="lastName"
                maxlength="50"
                :label="`${t('privateLastNameLabel')}`"
                required
                :placeholder="t('privateLastNamePlaceholder')"
                :errors="errors"></oie-base-input-v2>
            </validation-provider>

            <validation-provider
              v-if="isCompanyCustomer && selectedActiveCountry && selectedActiveCountry.useVatCode"
              v-slot="{errors}"
              :debounce="500"
              :rules="createVatNumberValidateRule"
              slim>
              <oie-vat-input
                v-if="isCompanyCustomer && selectedActiveCountry && selectedActiveCountry.useVatCode"
                v-model="vatNumber"
                :errors="errors"
                :country-code="selectedActiveCountry ? selectedActiveCountry.value : null">
              </oie-vat-input>
            </validation-provider>

            <validation-provider
              v-slot="{errors}"
              rules="required|mobile:^[0-9+]+$"
              slim
              mode="lazy">
              <oie-phone-input
                v-model="phone"
                required
                :errors="errors"
                :country-code="selectedActiveCountry ? selectedActiveCountry.callingCode : null">
              </oie-phone-input>
            </validation-provider>
          </div>

          <div class="input-column__container">
            <h3 class="input-column__header">
              {{ t('addressHeader') }}
            </h3>

            <oie-address-component id="register-signup" :address="invoiceAddress" :use-country-dropdown="false"></oie-address-component>
          </div>
        </div>

        <div class="input-column">
          <div class="input-column__container">
            <template v-if="isCompanyCustomer">
              <h3 class="input-column__header">
                {{ t('userDetailsHeader') }}
              </h3>

              <validation-provider
                v-slot="{errors}"
                rules="required|invalidchars"
                slim>
                <oie-base-input-v2
                  v-if="isCompanyCustomer"
                  v-model="firstName"
                  maxlength="49"
                  required
                  :label="`${t('privateNameLabel')}`"
                  :placeholder="t('privateNamePlaceholder')"
                  :errors="errors"></oie-base-input-v2>
              </validation-provider>

              <validation-provider
                v-slot="{errors}"
                rules="required|invalidchars"
                slim>
                <oie-base-input-v2
                  v-if="isCompanyCustomer"
                  v-model="lastName"
                  maxlength="50"
                  required
                  :label="`${t('privateLastNameLabel')}`"
                  :placeholder="t('privateLastNamePlaceholder')"
                  :errors="errors"></oie-base-input-v2>
              </validation-provider>
            </template>

            <h3 class="input-column__header">
              {{ t('accountInformationHeader') }}
            </h3>

            <validation-provider
              v-if="!noemail"
              v-slot="{errors}"
              rules="required|email|invalidchars"
              slim>
              <oie-base-input-v2
                v-model="username"
                required
                :label="`${tStatic('email')}`"
                :placeholder="tStatic('emailPlaceholder')"
                maxlength="80"
                :errors="errors"></oie-base-input-v2>
            </validation-provider>

            <oie-base-radio-checkbox
              v-if="clientModel.impersonating"
              v-model="noemail"
              label="Kund saknar epostadress">
            </oie-base-radio-checkbox>

            <fieldset>
              <legend class="sr-only">
                Nytt lösenord
              </legend>
              <oie-base-radio-checkbox
                v-if="clientModel.impersonating && !noemail"
                id="password-send"
                v-model="passwordReset"
                name="sendResetPassword"
                value="auto"
                label="Skicka automatiskt återställ lösenord epost"></oie-base-radio-checkbox>

              <oie-base-radio-checkbox
                v-if="clientModel.impersonating && !noemail"
                v-model="passwordReset"
                name="sendResetPassword"
                value="manual"
                label="Ange lösenord manuellt"></oie-base-radio-checkbox>
            </fieldset>

            <validation-provider
              v-if="!noemail && !sendResetPassword"
              v-slot="{errors}"
              vid="pw"
              rules="required|passwordPolicy"
              slim>
              <oie-base-input-v2
                v-model="password"
                type="password"
                required
                :label="`${t('passwordLabel')}`"
                :placeholder="t('passwordPlaceholder')"
                :errors="errors"
                :description="tStatic('passwordPolicy')"></oie-base-input-v2>
            </validation-provider>

            <validation-provider
              v-if="!noemail && !sendResetPassword"
              v-slot="{errors}"
              rules="required|password:pw"
              slim>
              <oie-base-input-v2
                v-model="reEnteredPassword"
                type="password"
                required
                :label="`${t('repeatPasswordLabel')}`"
                :placeholder="t('repeatPasswordPlaceholder')"
                :errors="errors"></oie-base-input-v2>
            </validation-provider>
          </div>

          <div class="input-column__actions">
            <oie-base-radio-checkbox
              v-if="!noemail"
              v-model="newsletter"
              class="input-column__checkbox"
              :label="t('newsletterLabel')">
            </oie-base-radio-checkbox>

            <validation-provider v-slot="{errors}" rules="checked" slim>
              <oie-base-radio-checkbox
                v-model="acceptTerms"
                class="input-column__checkbox"
                required
                :errors="errors"
                :label="t('approveTerms')"
                :description="t('approveTermsLabel')"></oie-base-radio-checkbox>

              <div
                v-if="clientModel.impersonating && !acceptTerms"
                class="superuser-terms-reminder">
                {{ tStatic('superUserCheckoutTermsReminder') }}
              </div>
            </validation-provider>

            <oie-base-action
              v-element-loader="{ endpoint: 'auth/create' }"
              class="register-container__register"
              size="large"
              @click="createAccount">
              {{ t('createAccountButtonLabel') }}
            </oie-base-action>
          </div>
        </div>
      </oie-layout-grid>
    </validation-observer>
  </div>
</template>

<script>
import httpService from '@/core/http-service'
import countryHandling from '@/mixins/country-handling'
import gtm from '@/core/enhanced-ecommerce-gtm'

export default {
  mixins: [countryHandling],
  data: () => ({
    selectedCustomerType: null,
    customerType: 'private',
    passwordReset: 'manual',
    username: null,
    password: null,
    customerName: null,
    firstName: null,
    lastName: null,
    phone: null,
    vatNumber: '',
    vatNumberCountryCode: null,
    newsletter: false,
    pdfInvoice: false,
    pdfInvoiceEmail: '',
    reEnteredPassword: null,
    acceptTerms: false,
    noemail: false,
    language: null,
    invoiceAddress: {
      addressLine1: null,
      addressCo: null,
      postalCode: null,
      city: null,
      countryCode: null,
      countryName: null
    }
  }),
  computed: {
    createVatNumberValidateRule() {
      return this.vatNumberCountryCode ? `vatNumber:${this.vatNumberCountryCode}` : null
    },
    createDefaultLanguageSelection() {
      if (this.clientModel.availibleLanguages.find(l => l.isDefault)) {
        return this.clientModel.availibleLanguages.find(l => l.isDefault).value
      }
      return this.clientModel.availibleLanguages[0].value
    },
    isCompanyCustomer() {
      return this.customerType === 'company'
    },
    sendResetPassword() {
      return this.passwordReset === 'auto'
    }
  },
  watch: {
    'invoiceAddress.countryCode'() {
      this.vatNumberCountryCode = this.invoiceAddress.countryCode
      this.setActiveCountry(this.invoiceAddress.countryCode)
    },
    'selectedCustomerType'() {
      this.customerType = this.selectedCustomerType
    },
    isCompanyCustomer() {
      this.vatNumber = ''
    }
  },
  methods: {
    async createAccount() {
      var isValidForm = await this.$refs.observer.validate()

      if(this.clientModel.impersonating && isValidForm) {
        var validCheck
        var warnMessage
        await httpService.post('auth/validate', {
          username: this.username,
          phone: this.phone,
          vatNumber: this.vatNumber,
          noEmail: this.noemail
        }).then((response) => {
          if (!response.data.success) {
            validCheck = false
            warnMessage = response.data.message
          }else {
            validCheck = true
          }
        })

        if(!validCheck) {
          await new Promise((resolve, reject) => {
            this.triggerModal(warnMessage, resolve, reject)
          })
        }
      }

      this.$refs.observer.validate().then((valid) => {
        if(valid) {
          httpService.post('auth/create', {
            isCompanyCustomer: this.isCompanyCustomer,
            username: this.username,
            password: this.password,
            customerName: this.customerName,
            firstName: this.firstName,
            lastName: this.lastName,
            phone: this.phone,
            addressLine1: this.invoiceAddress.addressLine1,
            addressCo: this.invoiceAddress.addressCo,
            postalCode: this.invoiceAddress.postalCode,
            city: this.invoiceAddress.city,
            countryName: this.invoiceAddress.countryName,
            countryCode: this.invoiceAddress.countryCode,
            vatNumber: this.vatNumber,
            newsletter: this.newsletter,
            pdfInvoice: this.pdfInvoice,
            noEmail: this.noemail,
            sendResetPassword: this.sendResetPassword,
            language: this.language
          })
            .then(({ data }) => {
              gtm.addSignUpEvent(this.isCompanyCustomer === null ? false : this.isCompanyCustomer)
              location.replace(data.url)
            })
            .catch((error) => {
              console.error('failed creating user', error)
            })
        }
      })
    },
    triggerModal(message, resolve, reject) {
      this.publish('modal.show.generic', {
        header: this.tStatic('superUserWarningMessageHeader'),
        text: message,
        buttons: [ {
          text: this.tStatic('superUserContinueButton'),
          usePrimaryButton: true,
          handler: () => {
            this.publish('modal.hide.generic')
            resolve()
          }
        }]
      })
    }
  }
}
</script>

<style lang="scss">
.input-column {
  @include media(small) {
    max-width: 32.5rem;
  }
}

.input-column > *:not(:last-child) {
  margin-bottom: measure(full);
}

.input-column__container > *:not(:last-child) {
  margin-bottom: measure(half);
}

.input-column__container--register {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;

  > *:not(:last-child) {
    margin-right: measure(full);
  }
}

.input-column__header {
  @include body-semi-bold;
  color: color(text-blue);
  margin-bottom: measure(half);
}

.input-column__actions {
  display: flex;
  flex-direction: column;

  .input {
    margin-bottom: measure(full);
  }
}

.input-column__actions > *:last-child {
  margin-bottom: 0;
}

.input-column__checkbox {
  margin-bottom: measure(half);
}

.input-column__button {
  width: 100%;

  @include media(small) {
    width: 18.125rem;
  }
}

.superuser-terms-reminder {
  background-color: #ffa;
  border: #999 1px solid;
  text-align: center;
  padding: measure(quarter);
  margin-bottom: measure(full);
}

.register-container__register {
  align-self: flex-start;
}

.customer-type-error {
  flex-basis: 100%;
  margin-bottom: 1rem;
}

</style>
